import React, { useState, useEffect } from 'react';
import { THEME } from '../../utils/theme';
import api from '../../utils/api';
import Spinner from '../ui/Spinner';

const FriendsActivityModal = ({ onClose }) => {
  const [loading, setLoading] = useState(true);
  const [friendsData, setFriendsData] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'playing', 'online'

  useEffect(() => {
    api.get('/api/integrations/steam/friends')
      .then(setFriendsData)
      .catch(err => setFriendsData({ available: false, reason: err.message }))
      .finally(() => setLoading(false));
  }, []);

  const filteredFriends = friendsData?.friends?.filter(f => {
    if (filter === 'playing') return f.is_playing;
    if (filter === 'online') return f.persona_state > 0;
    return true;
  }) || [];

  const personaStateLabel = (state) => {
    const states = { 0: 'Offline', 1: 'Online', 2: 'Busy', 3: 'Away', 4: 'Snooze', 5: 'Looking to trade', 6: 'Looking to play' };
    return states[state] || 'Unknown';
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(5,5,10,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(20px) saturate(180%)', WebkitBackdropFilter: 'blur(20px) saturate(180%)' }} onClick={onClose}>
      <div style={{ background: `linear-gradient(145deg, ${THEME.bgElevated}, ${THEME.bgCard})`, borderRadius: '20px', padding: '28px', width: '95%', maxWidth: '600px', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', border: `1px solid ${THEME.border}` }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, color: THEME.text, fontSize: '22px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>👥</span> Steam Friends
          </h2>
          <button onClick={onClose} style={{ background: THEME.bgHover, border: 'none', color: THEME.textMuted, width: '32px', height: '32px', borderRadius: '8px', fontSize: '18px', cursor: 'pointer' }}>&times;</button>
        </div>

        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px', gap: '12px' }}>
            <Spinner size={24} />
            <span style={{ color: THEME.textMuted }}>Loading friends...</span>
          </div>
        ) : !friendsData?.available ? (
          <div style={{ padding: '40px', textAlign: 'center', color: THEME.textMuted }}>
            <span style={{ fontSize: '40px', marginBottom: '16px', display: 'block' }}>😕</span>
            {friendsData?.reason || 'Could not load friends'}
          </div>
        ) : (
          <>
            {/* Stats bar */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <div style={{ flex: 1, background: THEME.bg, borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: THEME.success }}>{friendsData.playing_count}</div>
                <div style={{ fontSize: '10px', color: THEME.textMuted }}>Playing</div>
              </div>
              <div style={{ flex: 1, background: THEME.bg, borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: THEME.info }}>{friendsData.online_count}</div>
                <div style={{ fontSize: '10px', color: THEME.textMuted }}>Online</div>
              </div>
              <div style={{ flex: 1, background: THEME.bg, borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: THEME.textMuted }}>{friendsData.friends?.length || 0}</div>
                <div style={{ fontSize: '10px', color: THEME.textMuted }}>Total</div>
              </div>
            </div>

            {/* Filter tabs */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', background: THEME.bg, padding: '4px', borderRadius: '10px' }}>
              {[['all', 'All'], ['playing', '🎮 Playing'], ['online', '🟢 Online']].map(([key, label]) => (
                <button key={key} onClick={() => setFilter(key)} style={{
                  flex: 1, background: filter === key ? THEME.accent : 'transparent', color: filter === key ? '#000' : THEME.textMuted,
                  border: 'none', borderRadius: '8px', padding: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '12px'
                }}>{label}</button>
              ))}
            </div>

            {/* Friends list */}
            <div style={{ flex: 1, overflow: 'auto' }}>
              {filteredFriends.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: THEME.textMuted }}>
                  No friends {filter === 'playing' ? 'currently playing' : filter === 'online' ? 'online' : 'found'}
                </div>
              ) : (
                filteredFriends.map((friend, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: '12px', padding: '12px',
                    borderBottom: i < filteredFriends.length - 1 ? `1px solid ${THEME.border}` : 'none',
                    background: friend.is_playing ? `${THEME.success}08` : 'transparent'
                  }}>
                    <img
                      src={friend.avatar || '/default-avatar.png'}
                      alt=""
                      style={{ width: '40px', height: '40px', borderRadius: '8px', border: `2px solid ${friend.is_playing ? THEME.success : friend.persona_state > 0 ? THEME.info : THEME.border}` }}
                      onError={(e) => { e.target.src = '/default-avatar.png'; }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: THEME.text, fontWeight: '600', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {friend.persona_name}
                      </div>
                      <div style={{ color: friend.is_playing ? THEME.success : THEME.textMuted, fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {friend.is_playing ? `Playing: ${friend.game_name}` : personaStateLabel(friend.persona_state)}
                      </div>
                    </div>
                    {friend.is_playing && (
                      <div style={{ background: THEME.success, color: '#000', padding: '4px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '600' }}>
                        🎮 IN-GAME
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FriendsActivityModal;
